/**
 * Bot Service
 *
 * Manages bot Firebase accounts and bot matching for multiplayer.
 * Bots are stored as regular users with accountType: 'bot'.
 *
 * ELO source of truth: `ai_model_ratings` collection (shared with AI Arena).
 * Arena games (AI vs AI) and online games (human vs bot) both read/write
 * the same ratings, so bots calibrate naturally against each other and humans.
 */

import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { UserProfile, MMRData, TimeControlRating } from '../MMR/types';
import { BOT_DEFINITIONS, BOT_UID_PREFIX } from './botDefinitions';
import { BotDefinition, BotConfig } from './types';
import { AIArenaFirebaseService } from '../AIArena/AIArenaFirebaseService';

/** Check if a uid belongs to a bot account */
export function isBotAccount(uid: string): boolean {
  return uid.startsWith(BOT_UID_PREFIX);
}

/** Get the bot definition for a given uid (from local config, no Firebase needed) */
export function getBotDefinition(uid: string): BotDefinition | undefined {
  return BOT_DEFINITIONS.find(bot => bot.uid === uid);
}

/** Get bot config for a given uid */
export function getBotConfig(uid: string): BotConfig | undefined {
  return getBotDefinition(uid)?.config;
}

/** Convert a bot definition to its arena variant ID (e.g. "gaia-fast") */
export function botToVariantId(bot: BotDefinition): string {
  return `${bot.config.aiModel}-${bot.config.timeFrame}`;
}

/**
 * Find the best compatible bot for a player based on time control and ELO.
 * Returns the bot definition closest to the player's ELO that is allowed
 * in the given time control.
 *
 * Reads ELO from arena ratings (single source of truth) in one batch call.
 */
export async function findCompatibleBot(
  timeControl: 'sprint' | 'standard' | 'marathon',
  playerElo: number
): Promise<{ definition: BotDefinition; currentElo: number } | null> {
  // Filter bots by time control compatibility
  const compatibleBots = BOT_DEFINITIONS.filter(bot =>
    bot.config.allowedTimeControls.includes(timeControl)
  );

  if (compatibleBots.length === 0) return null;

  // Fetch ALL arena ratings in one call (instead of N individual calls)
  let arenaRatings: Map<string, number> = new Map();
  try {
    const allRatings = await AIArenaFirebaseService.getModelRatings();
    for (const r of allRatings) {
      if (r.games > 0) arenaRatings.set(r._docId, r.rating);
    }
  } catch (error) {
  }

  const botsWithElo = compatibleBots.map(bot => {
    const variantId = botToVariantId(bot);
    const elo = arenaRatings.get(variantId) ?? bot.config.startingElo;
    return { definition: bot, currentElo: elo };
  });

  // Sort by closest ELO to player
  botsWithElo.sort((a, b) =>
    Math.abs(a.currentElo - playerElo) - Math.abs(b.currentElo - playerElo)
  );

  // Reject if closest bot is still more than 300 ELO away
  const FALLOFF_THRESHOLD = 300;
  if (Math.abs(botsWithElo[0].currentElo - playerElo) > FALLOFF_THRESHOLD) {
    return null;
  }

  return botsWithElo[0];
}

/**
 * Get a bot's current ELO from arena ratings (single source of truth).
 * Falls back to starting ELO if no arena data exists.
 */
async function getBotCurrentElo(
  botUid: string,
  _timeControl: 'sprint' | 'standard' | 'marathon'
): Promise<number> {
  const def = getBotDefinition(botUid);
  if (!def) return 1300;

  const variantId = botToVariantId(def);

  try {
    // Read from arena ratings (single source of truth)
    const allRatings = await AIArenaFirebaseService.getModelRatings();
    const arenaRating = allRatings.find(r => r._docId === variantId);
    if (arenaRating && arenaRating.games > 0) {
      return arenaRating.rating;
    }
  } catch (error) {
  }

  // Fallback to starting ELO if no arena data
  return def.config.startingElo;
}

/**
 * Create a TimeControlRating entry for a bot at its starting ELO.
 * Bots start as "placed" (skip placement phase).
 */
function createBotTimeControlRating(startingElo: number): TimeControlRating {
  return {
    rating: startingElo,
    games: 0,
    peak: startingElo,
    isPlaced: true,
    placementGames: 10, // Skip placement
    provisionalRating: startingElo,
  };
}

/**
 * Initialize all bot accounts in Firebase.
 * Creates or updates user documents for each bot in BOT_DEFINITIONS.
 * Seeds ELO from arena ratings when available (preferred source of truth).
 * Safe to run multiple times (idempotent for new bots).
 */
export async function initializeAllBots(): Promise<void> {

  // Pre-fetch arena ratings for ELO seeding
  let arenaRatings: Map<string, number> = new Map();
  try {
    const allRatings = await AIArenaFirebaseService.getModelRatings();
    for (const r of allRatings) {
      if (r.games > 0) arenaRatings.set(r._docId, r.rating);
    }
  } catch {
  }

  for (const bot of BOT_DEFINITIONS) {
    try {
      // Use arena ELO if available, otherwise fallback to config
      const variantId = botToVariantId(bot);
      const startingElo = arenaRatings.get(variantId) ?? bot.config.startingElo;

      const userRef = doc(db, 'users', bot.uid);
      const existing = await getDoc(userRef);

      if (existing.exists()) {
        // Always update username and ELO from arena on re-init
        const tcRating = createBotTimeControlRating(startingElo);
        await updateDoc(userRef, {
          'profile.username': bot.username,
          'profile.mmr.provisionalRating': startingElo,
          'profile.mmr.displayRating': startingElo,
          'profile.mmr.peak': startingElo,
          'profile.mmr.timeControlRatings.sprint': tcRating,
          'profile.mmr.timeControlRatings.standard': tcRating,
          'profile.mmr.timeControlRatings.marathon': tcRating,
          'botConfig': bot.config,
        });
        continue;
      }

      const tcRating = createBotTimeControlRating(startingElo);

      const mmr: MMRData = {
        provisionalRating: startingElo,
        placementGames: 10,
        placementHistory: [],
        displayRating: startingElo,
        rankedGames: 0,
        peak: startingElo,
        timeControlRatings: {
          sprint: createBotTimeControlRating(startingElo),
          standard: createBotTimeControlRating(startingElo),
          marathon: createBotTimeControlRating(startingElo),
        },
        isPlaced: true,
        totalGames: 0,
        createdAt: new Date(),
        colorBalance: 0,
      };

      const profile: UserProfile = {
        mmr,
        username: bot.username,
        lastActive: new Date(),
        region: 'NA',
        regionDetectedBy: 'unknown',
        isPremium: false,
        chatEnabled: false,
      };

      const userData = {
        uid: bot.uid,
        profile,
        gamesPlayed: 0,
        createdAt: new Date(),
        lastActive: new Date(),
        accountType: 'bot',
        botConfig: bot.config,
      };

      await setDoc(userRef, userData);
    } catch (error) {
    }
  }

}
